/* eslint-disable @typescript-eslint/camelcase */
import {
  BulkBookingSuccess,
  BookedSttSuccess,
  BulkBookingEntities,
  BulkBookingData,
  BulkBookingResiEntities,
  BulkBookingResiData,
  ClientBookingTemplateDetail,
  HeaderMapping,
  ClientBookingEntities,
  ClientBookingListData,
  DetailRepairBulkBooking,
  BookingData,
  ErrorBookingData,
  CommodityData,
  DestinationCity,
  PostalCodeDestination,
  OriginCity,
  ClientData
} from "@/domain/entities/BulkBooking";
import { Pagination } from "@/domain/entities/Pagination";
import { AxiosResponse } from "axios";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";

export class BulkBookingMapper {
  public convertSuccessDataFromApi(
    res: AxiosResponse<any>
  ): BulkBookingSuccess {
    const {
      data: { data }
    } = res;
    return new BulkBookingSuccess(
      data.bulk_id,
      data.stt_success.map(
        (stt: any) =>
          new BookedSttSuccess(
            stt.manual_stt_number,
            stt.origin_district_code,
            stt.destination_district_code,
            stt.shipper_name,
            stt.shipper_address,
            stt.shipper_phone,
            stt.consignee_name,
            stt.consignee_address,
            stt.consignee_phone,
            stt.total_pieces,
            stt.total_weight,
            stt.commodity_code,
            stt.product_type,
            stt.insurance_service_type,
            Number(stt.value_of_goods),
            stt.external_number,
            stt.cod,
            stt.woodpacking,
            Number(stt.ppn),
            Number(stt.pph),
            Number(stt.shipping_rate),
            Number(stt.insurance_rate),
            Number(stt.total_surcharge),
            Number(stt.total_amount),
            Number(stt.bm)
          )
      )
    );
  }

  public convertFailedExcelFromApi(result: AxiosResponse): string {
    try {
      const { data } = result;
      return data;
    } catch (e) {
      return "";
    }
  }

  public convertBulkBookingResiFromApi(
    res: AxiosResponse<any>
  ): BulkBookingResiEntities {
    const { data } = res;
    const bulkBookingResiData: BulkBookingResiData[] = [] || null;
    if (data.data === []) {
      [];
    } else {
      data.data.forEach((item: any) => {
        bulkBookingResiData.push(
          new BulkBookingResiData(item.stt_no, item.stt_id)
        );
      });
    }

    return new BulkBookingResiEntities(bulkBookingResiData);
  }

  public convertUploadBulkBookingFromApi(
    result: AxiosResponse
  ): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.data.bulk_booking_status
    });
  }

  public convertListBulkBookingFromApi(
    result: AxiosResponse
  ): BulkBookingEntities {
    const { data } = result;
    return new BulkBookingEntities(
      new Pagination(data.meta.page, data.meta.limit, data.meta.total_records),
      data.data.length
        ? data.data.map(
            (item: any) =>
              new BulkBookingData(
                item.upload_id,
                item.activity_name,
                item.archive_type.toLowerCase() === "stt"
                  ? "Bulk Booking"
                  : item.archive_type,
                item.created_name,
                item.created_code,
                item.created_at,
                item.created_by,
                item.total_stt ? item.total_stt : 0,
                item.total_stt_success ? item.total_stt_success : 0,
                item.status,
                item.archive_file,
                item.failed_file,
                this.getSaveValueOrString(item.created_type),
                this.getSaveValueOrNumber(item.data_processed_current),
                this.getSaveValueOrNumber(item.total_data_current),
                this.getSaveValueOrNumber(item.total_stt_repaired),
                this.getSaveValueOrNumber(item.created_id),
                this.getSaveValueOrString(item.file_name)
              )
          )
        : []
    );
  }

  public convertListClientBookingFromApi(
    result: AxiosResponse
  ): ClientBookingEntities {
    const { data } = result;
    return new ClientBookingEntities(
      new Pagination(data.meta.page, data.meta.limit, data.meta.total_records),
      data.data.length
        ? data.data.map((item: any) => {
            return new ClientBookingListData({
              id: item.config_id,
              accountName: item.account_ref_name,
              accountId: item.account_ref_code,
              createdBy: item.created_name,
              createdAt: item.created_at,
              updatedBy: item.updated_name,
              updatedAt: item.updated_at
            });
          })
        : []
    );
  }

  public convertDetailClientBookingFromApi(
    result: AxiosResponse
  ): ClientBookingTemplateDetail {
    const { data } = result;
    return new ClientBookingTemplateDetail({
      id: data.data.id_config,
      accountRefId: data.data.account_ref_id,
      accountRefType: data.data.account_ref_type,
      accountRefName: data.data.account_ref_name,
      accountRefCode: data.data.account_ref_code,
      createdAt: data.data.created_at,
      createdBy: data.data.created_by_name,
      updatedAt: data.data.updated_at,
      updatedBy: data.data.updated_by_name,
      headerMapping: data.data.header_mapping.map((item: any) => {
        return new HeaderMapping({
          genesisHeader: item.genesis_header,
          sourceHeader: item.source_header,
          activitySelect: false
        });
      })
    });
  }

  public convertEditClientTemplateConfigurationToMapper(
    result: AxiosResponse<any>
  ): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertCreateClientTemplateConfigurationToMapper(
    result: AxiosResponse<any>
  ): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  private getSaveValueOrString(value: any) {
    return value || ""
  }

  private getSaveValueOrNumber(value: any) {
    return value || 0
  }

  public convertDetailRepairBulkBookingToMapper(
    result: AxiosResponse<any>
  ): DetailRepairBulkBooking {
    const { data } = result.data;

    return new DetailRepairBulkBooking({
      ...this.extractMainBookingInfo(data),
      bookingData: this.mapBookingData(data.data, data.booked_type),
      errorBookingData: this.mapErrorBookingData(data.error)
    });
  }

  private extractMainBookingInfo(data: any) {
    return {
      rowNumber: data.row_number,
      status: data.status,
      totalRepaired: data.total_repaired,
      totalFailed: data.total_failed,
      repairPosition: data.repair_position,
      bookedType: data.booked_type,
      clientId: data.client_id,
      partnerId: data.partner_id
    };
  }

  private mapBookingData(data: any, bookedType: string): BookingData {
    return new BookingData({
      shipmentId: data.shipment_id,
      externalNumber: data.external_number,
      manualSttNumber: data.manual_stt_number,
      destinationDistrictName: data.destination_district_name,
      destinationPostalCode: data.destination_postal_code,
      shipperName: data.shipper_name,
      shipperAddress: data.shipper_address,
      shipperPhoneNumber: data.shipper_phone_number,
      consigneeName: data.consignee_name,
      consigneeAddress: data.consignee_address,
      consigneesAddressType: ["rumah", "home"].includes(
        data.consignee_address_type.toLowerCase()
      )
        ? "home"
        : "office",
      consigneePhone: data.consignee_phone,
      totalPieces: data.total_pieces,
      totalWeight: data.total_weight,
      commodityCode: data.commodity_code,
      productType: data.product_type,
      valueOfGoods: data.value_of_goods,
      insuranceFlag: data.insurance_flag === "Y",
      serviceType: data.service_type,
      codAmount: data.cod_amount,
      originDistrictName: data.origin_district_name,
      commodity: this.mapCommodityData(data.commodity),
      destinationCity: data.destination_district
        ? this.mapDestinationCity(data.destination_district)
        : new DestinationCity(),
      postalCodeDestination: data.destination_district
        ? this.mapPostalCodeDestination(data.destination_district, data.destination_postal_code)
        : new PostalCodeDestination(),
      originCity: data.origin_district
        ? this.mapOriginCity(data.origin_district)
        : new OriginCity(),
      clientData:
        bookedType === "client"
          ? this.mapClientData(data.client)
          : new ClientData()
    });
  }

  private mapCommodityData(commodity: any): CommodityData {
    if (!commodity) return new CommodityData();
    return new CommodityData({
      id: commodity.commodity_id,
      code: commodity.commodity_code,
      name: commodity.commodity_name,
      status: commodity.commodity_status,
      isDangerousGoods: commodity.is_dangerous_goods,
      isQuarantine: commodity.commodity_is_quarantine
    });
  }

  private mapDestinationCity(district: any): DestinationCity {
    return new DestinationCity({
      cityId: district.city.id,
      id: district.id,
      name: district.name + ", " + district.city.name,
      status: district.status,
      code: district.code,
      cityCode: district.city.code,
      vendorCode: district.vendor_code,
      type: district.type,
      isCod: district.is_cod,
      countryCode: district.country.code,
      countryName: district.country.name,
      postalCode: district.destination_zip_code
    });
  }

  private mapPostalCodeDestination(district: any, postalCode: string): any {
    return postalCode ? new PostalCodeDestination({
      id: district.id,
      name: postalCode
    }) : ""
  }

  private mapOriginCity(district: any): OriginCity {
    return new OriginCity({
      id: district.id,
      cityId: district.city.id,
      cityCode: district.city.code,
      name: district.name,
      code: district.code,
      isCod: district.is_cod,
      status: district.status,
      type: district.type,
      vendorCode: district.vendor_code
    });
  }

  private mapClientData(client: any): ClientData {
    const clientName = client.client_alias
      ? `${client.client_alias} - ${client.client_name}`
      : client.client_name;

    return new ClientData({
      id: client.client_id,
      name: clientName,
      originalClientName: client.client_name,
      status: client.client_is_banned,
      isDO: client.client_is_dfod,
      isCOD: client.client_is_cod,
      clientCode: client.client_code,
      clientOriginCity: client.client_origin_city,
      clientCategory: client.client_category,
      clientPackageType: client.client_package_type,
      clientCodConfigurationData: client.client_cod_config_amount_data,
      clientCodConfigAmountData: client.client_cod_config_amount_data,
      clientAlias: client.client_alias
    });
  }

  private mapErrorBookingData(error: any): ErrorBookingData {
    return new ErrorBookingData({
      shipmentId: error.shipment_id,
      externalNumber: error.external_number,
      manualSttNumber: error.manual_stt_number,
      destinationDistrictName: error.destination_district_name,
      destinationPostalCode: error.destination_postal_code,
      shipperName: error.shipper_name,
      shipperAddress: error.shipper_address,
      shipperPhoneNumber: error.shipper_phone,
      consigneeName: error.consignee_name,
      consigneeAddress: error.consignee_address,
      consigneesAddressType: error.consignee_address_type,
      consigneePhone: error.consignee_phone,
      totalPieces: error.total_pieces,
      totalWeight: error.total_weight,
      commodityCode: error.commodity_code,
      productType: error.product_type,
      valueOfGoods: error.value_of_goods,
      insuranceFlag: error.insurance_flag,
      serviceType: error.service_type,
      codAmount: error.cod_amount,
      originDistrictName: error.origin_district_name
    });
  }
}
